import React, { useCallback, useEffect, useState } from "react";
import { Activity, Loader2, Inbox, Building2, Tag, Sparkles } from "lucide-react";
import { visitsApi } from "@/lib/api/visits";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch (_) { return iso.slice(0, 10); }
}

export default function MemberVisitsPage() {
  const { lang, t } = useI18n();
  const tr = (en, ar) => lang === "ar" ? ar : en;

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const data = await visitsApi.myHistory({ limit: pageSize, offset: (page - 1) * pageSize });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      toast.error(msg || tr("Failed to load visits", "فشل تحميل الزيارات"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pageSize]);

  return (
    <div className="max-w-2xl space-y-6 fade-up" data-testid="page-member-visits">
      <header>
        <p className="text-sm font-medium text-primary">{tr("Your history", "سجلك")}</p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">{tr("Visit History", "سجل الزيارات")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {tr("All your visits and redeemed deals.", "جميع زياراتك والعروض التي استخدمتها.")}
        </p>
      </header>

      <TableToolbar pageSize={pageSize} onPageSizeChange={setPageSize} />

      {items === null ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="surface-card p-12 flex flex-col items-center gap-3 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            {tr("No visits yet. Start using your card at partner providers!", "لم تسجل أي زيارات بعد.")}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((v) => (
              <div key={v.id} className="surface-card p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground truncate">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">{v.provider_name || tr("Provider", "المزود")}</span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtDate(v.visited_at, lang)}</span>
                  </div>
                  {v.offer_id ? (
                    <div className="flex items-center gap-1.5 text-xs text-primary mt-0.5">
                      <Tag className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {lang === "ar" ? v.offer_title_ar || v.offer_title_en : v.offer_title_en || v.offer_title_ar}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5 italic">
                      {tr("Visit only", "زيارة فقط")}
                    </p>
                  )}
                  {(v.discount_amount != null && v.discount_amount > 0) || v.actual_invoice_amount != null ? (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                      {v.discount_amount != null && v.discount_amount > 0 && (
                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                          <Sparkles className="h-3 w-3" />
                          {tr(
                            `Saved ${Math.round(v.discount_amount).toLocaleString()} EGP`,
                            `وفرت ${Math.round(v.discount_amount).toLocaleString()} ج.م`
                          )}
                        </span>
                      )}
                      {v.actual_invoice_amount != null && (
                        <span className="text-muted-foreground">
                          {tr(
                            `Bill ${Number(v.actual_invoice_amount).toLocaleString()} EGP`,
                            `الفاتورة ${Number(v.actual_invoice_amount).toLocaleString()} ج.م`
                          )}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className="surface-card">
            <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
          </div>
        </>
      )}
    </div>
  );
}
